import { useEffect, useRef, useState } from "react";
import {
  formatPromptContextDebugLines,
  parsePromptContextDebugMessage,
} from "../lib/format-generation-debug-message.js";
import type { DevDebugLogLine } from "../lib/use-dev-debug-log.js";

type DevDebugPanelProps = {
  lines: DevDebugLogLine[];
  onClear: () => void;
};

function DebugLogMessage({ message }: { message: string }) {
  const promptContext = parsePromptContextDebugMessage(message);
  if (!promptContext) {
    return <span className="dev-debug-message">{message}</span>;
  }

  const lines = formatPromptContextDebugLines(promptContext);
  const label =
    promptContext.kind === "refine" ? "refine_prompt_context" : "create_prompt_context";

  return (
    <span className="dev-debug-message dev-debug-prompt-context">
      <span className="dev-debug-prompt-label">{label}</span>
      {lines.map((line) => (
        <span key={line} className="dev-debug-prompt-line">
          {line}
        </span>
      ))}
    </span>
  );
}

function formatTime(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function DevDebugPanel({ lines, onClear }: DevDebugPanelProps) {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open, lines.length]);

  return (
    <div className={`dev-debug-drawer ${open ? "is-open" : ""}`}>
      <div className="dev-debug-sheet">
        <button
          type="button"
          className="dev-debug-tab"
          aria-expanded={open}
          aria-controls="dev-debug-log-body"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="dev-debug-tab-grip" aria-hidden />
          <span className="dev-debug-tab-label">Debug log</span>
          <span className="dev-debug-count">{lines.length}</span>
          <span className="dev-debug-tab-chevron" aria-hidden>
            {open ? "Hide" : "Show"}
          </span>
        </button>

        <div className="dev-debug-toolbar">
          <span className="dev-debug-toolbar-note">DEV_DEBUG stream</span>
          <button type="button" className="btn ghost btn-compact" onClick={onClear}>
            Clear
          </button>
        </div>

        <div
          id="dev-debug-log-body"
          ref={scrollRef}
          className="dev-debug-body"
          role="log"
          aria-live="polite"
          aria-hidden={!open}
        >
          {lines.length === 0 ? (
            <p className="dev-debug-empty">Waiting for debug events…</p>
          ) : (
            lines.map((line, index) => (
              <div key={`${line.at}-${index}`} className="dev-debug-line">
                <span className="dev-debug-time">{formatTime(line.at)}</span>
                <span className="dev-debug-source">{line.source}</span>
                <DebugLogMessage message={line.message} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
